import { ethers } from 'ethers';
import { merkletree, merkletreeprivatekey, mintpriceinsol, RPC_ENDPOINT_L } from '../../constants/apiPaths';
import { createSignerFromKeypair, publicKey } from '@metaplex-foundation/umi';
// import { mintToCollectionV1 } from '@metaplex-foundation/mpl-bubblegum';
import { createUmi } from '@metaplex-foundation/umi-bundle-defaults';
import { walletAdapterIdentity } from '@metaplex-foundation/umi-signer-wallet-adapters';

import {
    Keypair,
    LAMPORTS_PER_SOL,
    PublicKey,
    SystemProgram,
    TransactionMessage,
    VersionedTransaction,
} from '@solana/web3.js';
import { toWeb3JsInstruction } from '@metaplex-foundation/umi-web3js-adapters';
import { getRpcUrl } from './getRPCURL';
import { getMetaMaskProvider } from '../../blockchainInteraction/finaliseGrading';
// const walletAdapterIdentity = {};
/*
  transfer SOL equal to $5 to the treasury
  mint one NFT to user's wallet
  requirements: treasury publickey, tree creator keypair, user keypair,
*/

const mintToCollectionV1 = () => {};
export const getMintPriceForEVM = async (contractAddress, chainId) => {
    const provider = new ethers.JsonRpcProvider(getRpcUrl(chainId));

    const contractABI = ['function mintPrice() public view returns (uint256)'];

    const contract = new ethers.Contract(contractAddress, contractABI, provider);

    try {
        const rawPrice = await contract.mintPrice();

        const decimals = 18;

        const mintPrice = Number(rawPrice) / Math.pow(10, decimals);

        return mintPrice;
    } catch (error) {
        throw error;
    }
};

export const getMintTransaction = async (
    metadataUri,
    nftName,
    collectionMintString,
    treasuryAddress,
    connection,
    wallet // useWallet() passed here
) => {
    const phantomAddressPublicKey = wallet.publicKey;
    const collectionMint = publicKey(collectionMintString);
    const treasury = new PublicKey(treasuryAddress);
    const merkleTree = publicKey(merkletree);
    const mintPriceInSol = Number(mintpriceinsol);

    const umi = createUmi(RPC_ENDPOINT_L);
    umi.use(walletAdapterIdentity(wallet));

    const keypair1 = Keypair.fromSecretKey(new Uint8Array(merkletreeprivatekey?.split(',')?.map(Number)));

    const treeCreatorKeypair = umi.eddsa.createKeypairFromSecretKey(keypair1.secretKey);
    const treeCreatorKeypairSigner = createSignerFromKeypair(umi, treeCreatorKeypair);

    const transferInstruction = SystemProgram.transfer({
        fromPubkey: phantomAddressPublicKey,
        toPubkey: treasury,
        lamports: mintPriceInSol * LAMPORTS_PER_SOL,
    });

    const mintInstructions = mintToCollectionV1(umi, {
        leafOwner: phantomAddressPublicKey,
        merkletree,
        collectionMint,
        metadata: {
            name: nftName,
            uri: metadataUri,
            sellerFeeBasisPoints: 0,
            symbol: 'PILL',
            collection: { key: collectionMint, verified: false },
            creators: [
                {
                    address: treeCreatorKeypair.publicKey,
                    verified: false,
                    share: 100,
                },
            ],
        },
        treeCreatorOrDelegate: treeCreatorKeypairSigner,
        collectionAuthority: treeCreatorKeypairSigner,
    })
        .getInstructions()
        .map(toWeb3JsInstruction);

    const blockhash = (await connection.getLatestBlockhash()).blockhash;

    const message = new TransactionMessage({
        payerKey: wallet.publicKey,
        recentBlockhash: blockhash,
        instructions: [transferInstruction, ...mintInstructions],
    }).compileToV0Message();

    const transaction = new VersionedTransaction(message);

    transaction.sign([Keypair.fromSecretKey(treeCreatorKeypair.secretKey)]);

    return transaction;
};

const contractABI = ['function mint() public payable', 'function mintPrice() public view returns (uint256)'];

export const mintNFT = async (contractAddress, chainId) => {
    // Configuration
    //const contractAddress = '0x064B8F36ED46592a61b463BE3DA635Aeb5B2D41F';
    //   const contractAddress = "0x292ED3701f74018117fe8cc29FE569259139BcE1"

    const metaMaskProvider = getMetaMaskProvider();

    try {
        // Request user accounts from MetaMask
        const accounts = await metaMaskProvider.request({
            method: 'eth_requestAccounts',
        });
        const from = accounts[0];

        // Wrap the raw provider with ethers.js Web3Provider and get the signer
        // const web3Provider = new ethers.providers.Web3Provider(metaMaskProvider)
        const web3Provider = new ethers.BrowserProvider(metaMaskProvider);
        const signer = await web3Provider.getSigner();

        // Create contract instance with the signer
        const nftContract = new ethers.Contract(contractAddress, contractABI, signer);

        // Get mint price from contract
        const mintPrice = await nftContract.mintPrice();

        // Encode the mint function call
        const mintData = new ethers.Interface(['function mint() public payable']).encodeFunctionData('mint', []);

        const transactionParams = {
            to: contractAddress, // Contract address
            from: from, // Connected account
            data: mintData, // Encoded function call
            value: '0x' + mintPrice.toString(16), // Payment required for minting
        };

        // Send the transaction using MetaMask's request method
        const result = await metaMaskProvider.request({
            method: 'eth_sendTransaction',
            params: [transactionParams],
        });

        return { success: true, result };
    } catch (error) {
        return { success: false, code: error?.message };
    }
};

// Use Phantom’s EVM provider
const getPhantomEvmProvider = () => {
    if (typeof window !== 'undefined' && window?.ethereum?.isPhantom) {
        return window.ethereum;
    }
    throw new Error('Phantom EVM provider not found. Make sure Phantom is installed and selected.');
};
